class CartGiftManager {
    constructor() {
      this.threshold = window.theme?.giftThreshold || 4900;
      this.giftVariantId = window.theme?.giftVariantId;
      this.giftSellingPlanId = window.theme?.giftSellingPlanId;
      this.initialized = false;
      this.isUpdating = false;
      this.updateTimeout = null;
      this.lastUpdateTime = 0;
      this.minUpdateInterval = 2000; // Increased to 2 seconds
      this.cartDrawerSelector = '#cartSlideoutWrapper';
      this.manuallyRemoved = false; // Track if gift was manually removed
      
      this.init();
    }
  
    async init() {
      if (this.initialized) return;
      
      this.setupEventListeners();
      this.setupMutationObserver();
      this.initialized = true;
      
      // Initial delay before first update
      await new Promise(resolve => setTimeout(resolve, 1000));
      await this.safeUpdateGiftStatus();
    }
  
    setupEventListeners() {
      const cartEvents = ['cart:updated', 'cart:refresh', 'product:added'];
      cartEvents.forEach(event => {
        document.addEventListener(event, async () => {
          // On cart update, check if our gift was removed
          const cart = await this.getCartState();
          if (cart) {
            const giftStillInCart = cart.items.some(item => 
              item.variant_id == this.giftVariantId && 
              item.properties?._gift === 'true'
            );
            if (!giftStillInCart) {
              console.log('Gift no longer in cart');
              this.manuallyRemoved = true;
              setTimeout(() => {
                this.manuallyRemoved = false;
              }, 3600000);
            }
          }
          this.debounceUpdate();
        });
      });
  
      // Handle quantity changes and remove button
      document.addEventListener('click', async (e) => {
        const removeButton = e.target.closest('#cart-item-remove-button, .cart-item-remove-button');
        const decreaseButton = e.target.closest('.cart-item-decrease');
        
        if (!removeButton && !decreaseButton) return;
        
        try {
          const cart = await this.getCartState();
          if (!cart) return;
          
          const cartItem = (removeButton || decreaseButton).closest('article.cart-item');
          if (!cartItem) return;
          
          // Get all cart items in the drawer
          const cartItems = Array.from(document.querySelectorAll('article.cart-item'));
          const itemIndex = cartItems.indexOf(cartItem);
          const currentItem = cart.items[itemIndex];
          
          // Check if this is our gift product
          if (currentItem?.variant_id == this.giftVariantId) {
            // If it's the remove button or if quantity will go to 0
            if (removeButton || (decreaseButton && currentItem.quantity === 1)) {
              console.log('Gift item being removed');
              this.manuallyRemoved = true;
              setTimeout(() => {
                this.manuallyRemoved = false;
              }, 3600000);
            }
          }
        } catch (error) {
          console.error('Error checking item modification:', error);
        }
      });

      // Handle direct quantity input
      document.addEventListener('change', async (e) => {
        const quantityInput = e.target.closest('.cart-item-quantity');
        if (!quantityInput) return;
        
        try {
          const cart = await this.getCartState();
          if (!cart) return;
          
          const cartItem = quantityInput.closest('article.cart-item');
          if (!cartItem) return;
          
          const cartItems = Array.from(document.querySelectorAll('article.cart-item'));
          const itemIndex = cartItems.indexOf(cartItem);
          const currentItem = cart.items[itemIndex];
          
          if (currentItem?.variant_id == this.giftVariantId) {
            const newQuantity = parseInt(quantityInput.value) || 0;
            if (newQuantity === 0) {
              console.log('Gift item quantity set to 0');
              this.manuallyRemoved = true;
              setTimeout(() => {
                this.manuallyRemoved = false;
              }, 3600000);
            }
          }
        } catch (error) {
          console.error('Error checking quantity change:', error);
        }
      });
    }
  
    setupMutationObserver() {
      const cartDrawer = document.querySelector(this.cartDrawerSelector);
      if (!cartDrawer) return;
  
      this.observer = new MutationObserver((mutations) => {
        if (this.isUpdating) return;
        
        // Only trigger update if there are relevant changes
        const hasRelevantChanges = mutations.some(mutation => {
          return mutation.type === 'childList' && 
                 !mutation.target.classList?.contains('gift-progress') &&
                 !mutation.target.classList?.contains('cart-gift-item');
        });
        
        if (hasRelevantChanges) {
          this.debounceUpdate(500);
        }
      });
  
      this.observer.observe(cartDrawer, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      });
    }

    debounceUpdate(delay = 1000) {
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }

      const now = Date.now();
      const timeSinceLastUpdate = now - this.lastUpdateTime;
      const actualDelay = Math.max(delay, this.minUpdateInterval - timeSinceLastUpdate);

      this.updateTimeout = setTimeout(async () => {
        if (!this.isUpdating) {
          await this.safeUpdateGiftStatus();
        }
      }, actualDelay);
    }
  
    async safeUpdateGiftStatus() {
      if (this.isUpdating) return;
      this.isUpdating = true;
      this.lastUpdateTime = Date.now();
  
      try {
        const cart = await this.getCartState();
        if (!cart) return;
  
        await this.handleGiftLogic(cart);
        await this.updateCartUI(cart);
      } catch (error) {
        if (error.message?.includes('429')) {
          // If we hit rate limit, try again after a longer delay
          setTimeout(() => this.debounceUpdate(3000), 3000);
        } else {
          console.error('Gift update error:', error);
        }
      } finally {
        this.isUpdating = false;
      }
    }
  
    async getCartState() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) {
          if (response.status === 429) {
            throw new Error('429: Rate limit exceeded');
          }
          throw new Error(`Cart fetch failed: ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        console.error('Cart fetch failed:', error);
        throw error;
      }
    }
  
    async handleGiftLogic(cart) {
      const { total_price: total, items } = cart;
      const shouldHaveGift = total >= this.threshold && !this.manuallyRemoved;
      const giftItem = items.find(item => 
        item.variant_id == this.giftVariantId && 
        item.properties?._gift === 'true'
      );
  
      // Only perform one cart modification at a time
      if (shouldHaveGift && !giftItem) {
        await this.modifyGiftInCart('add');
      } else if (!shouldHaveGift && giftItem) {
        await this.modifyGiftInCart('remove');
      } else if (giftItem && giftItem.quantity !== 1) {
        await this.modifyGiftInCart('adjust');
      }
    }
  
    async modifyGiftInCart(action) {
      const endpoint = action === 'add' ? '/cart/add.js' : '/cart/change.js';
      const payload = action === 'add' ? {
        items: [{
          id: this.giftVariantId,
          quantity: 1,
          properties: { _gift: 'true' },
          ...(this.giftSellingPlanId && { selling_plan: this.giftSellingPlanId })
        }]
      } : {
        id: this.giftVariantId,
        quantity: action === 'adjust' ? 1 : 0
      };
  
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          if (response.status === 429) {
            throw new Error('429: Rate limit exceeded');
          }
          throw new Error(`${action} failed: ${response.status}`);
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        await this.refreshCartDrawer();
      } catch (error) {
        console.error(`${action} gift failed:`, error);
        throw error;
      }
    }
  
    async refreshCartDrawer() {
      try {
        const cart = await this.getCartState();
        if (window.wetheme?.cartDrawer?.updateCartDrawer) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          window.wetheme.cartDrawer.updateCartDrawer(cart);
        }
      } catch (error) {
        console.error('Cart drawer refresh failed:', error);
        throw error;
      }
    }
  
    async updateCartUI(cart) {
      const progress = Math.min((cart.total_price / this.threshold) * 100, 100);
      const remaining = this.threshold - cart.total_price;
      const isThresholdMet = cart.total_price >= this.threshold;
  
      // Don't show gift UI if manually removed
      if (this.manuallyRemoved) {
        this.updateProgressElements(0, this.threshold, false);
        this.toggleGiftItem(false);
        this.updateCartDrawerLabels(false);
        return;
      }

      this.updateProgressElements(progress, remaining, isThresholdMet);
      this.toggleGiftItem(isThresholdMet);
      this.updateCartDrawerLabels(isThresholdMet);
    }
  
    updateProgressElements(progress, remaining, isThresholdMet) {
      document.querySelectorAll('.gift-progress').forEach(el => {
        el.style.width = `${progress}%`;
      });
      
      document.querySelectorAll('.gift-progress-remaining').forEach(el => {
        el.textContent = `$${(remaining / 100).toFixed(2)} remaining`;
        el.style.display = isThresholdMet ? 'none' : 'block';
      });
      
      document.querySelectorAll('.gift-progress-achieved').forEach(el => {
        el.style.display = isThresholdMet ? 'block' : 'none';
      });
      
      document.querySelectorAll('.gift-label').forEach(el => {
        el.classList.toggle('is-active', isThresholdMet);
      });
      
      document.querySelectorAll('.gift-threshold').forEach(el => {
        el.classList.toggle('is-completed', isThresholdMet);
      });
    }
  
    toggleGiftItem(visible) {
      document.querySelectorAll('.cart-gift-item').forEach(giftItem => {
        giftItem.style.display = visible ? 'flex' : 'none';
        giftItem.classList.toggle('is-visible', visible);
      });
    }
  
    updateCartDrawerLabels(isThresholdMet) {
      // Implementation of updateCartDrawerLabels method
    }
  }
  
  // Initialisation
  document.addEventListener('DOMContentLoaded', () => {
    new CartGiftManager();
  });
  